import { detectTrailingDate } from './parseNaturalDate'

/**
 * Inline shortcuts for the quick-add row (designs 18b, 25a):
 *
 * - `!high`, `!urgent`, `!p1`… set the priority
 * - `@me` assigns you, and `@name` assigns a workspace member (design 29a)
 * - `#label` adds an existing label (case-insensitive, multi-word names work)
 * - `@project` files it under an existing project, by name or key
 * - a trailing natural date ("tomorrow", "on Friday", "in 2 weeks") sets the
 *   due date
 *
 * A token only counts once it's followed by a space, so a half-typed "!hi" or
 * "#desi" is left alone, and a `#word` or `@word` with no matching label or
 * project stays in the title as typed.
 */
const PRIORITY_TOKENS: Record<string, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  med: 2,
  low: 1,
  p1: 4,
  p2: 3,
  p3: 2,
  p4: 1,
}

const PRIORITY_RE = /(^|\s)!(urgent|high|medium|med|low|p[1-4])(?=\s)/i
const ME_RE = /(^|\s)@me(?=\s)/i

/**
 * Single words chrono reads as dates that are far more often just words in a
 * title ("Meeting now", "Sun screen", "Sat on it"). Typed one at a time, each
 * would be at the trailing edge for a moment, so they never auto-apply.
 */
const AMBIGUOUS_DATE_WORDS = new Set(['now', 'sun', 'sat', 'mon', 'wed'])

export interface QuickAddLabel {
  id: string
  name: string
}

export interface QuickAddProject {
  id: string
  name: string
  projectKey?: string | null
}

export interface QuickAddMember {
  userId: string
  name?: string | null
  email?: string | null
}

export interface QuickAddContext {
  labels?: ReadonlyArray<QuickAddLabel>
  /** Members `@name` can assign. Checked before projects. */
  members?: ReadonlyArray<QuickAddMember>
  projects?: ReadonlyArray<QuickAddProject>
  /** Parse a trailing natural date. Off unless asked for. */
  parseDates?: boolean
  /** Reference "now" for date parsing (tests). */
  refDate?: Date
}

export interface QuickAddTokens {
  name: string
  priority?: number
  assignMe?: boolean
  /** A member typed as `@name`. */
  assigneeUserId?: string
  labelIds?: Array<string>
  projectId?: string
  dueDate?: Date
  /** True when the parsed date carries a time of day ("tomorrow at 3pm"). */
  dueHasTime?: boolean
}

interface NamedCandidate {
  id: string
  names: Array<string>
}

/**
 * Find the first `<sigil><name>` followed by whitespace, where name is one of
 * the candidates' names (case-insensitive). Longest name wins so "#needs
 * review" beats "#needs" when both exist. Returns the match span.
 */
function findSigilMatch(
  input: string,
  sigil: '#' | '@',
  candidates: Array<NamedCandidate>,
): { id: string; start: number; end: number } | null {
  if (candidates.length === 0) return null
  const lower = input.toLowerCase()
  let from = 0
  while (from < input.length) {
    const at = input.indexOf(sigil, from)
    if (at === -1) return null
    from = at + 1
    if (at > 0 && !/\s/.test(input[at - 1] ?? '')) continue
    let best: { id: string; start: number; end: number } | null = null
    for (const candidate of candidates) {
      for (const raw of candidate.names) {
        const name = raw.trim().toLowerCase()
        if (!name) continue
        const end = at + 1 + name.length
        if (lower.slice(at + 1, end) !== name) continue
        if (!/\s/.test(input[end] ?? '')) continue
        if (!best || end > best.end) best = { id: candidate.id, start: at, end }
      }
    }
    if (best) return best
  }
  return null
}

function cutSpan(input: string, start: number, end: number): string {
  return input.slice(0, start) + input.slice(end)
}

export function parseQuickAddTokens(
  input: string,
  context: QuickAddContext = {},
): QuickAddTokens {
  let name = input
  const result: QuickAddTokens = { name }

  const priority = PRIORITY_RE.exec(name)
  if (priority) {
    result.priority = PRIORITY_TOKENS[(priority[2] ?? '').toLowerCase()]
    name = name.replace(PRIORITY_RE, '$1')
  }
  if (ME_RE.test(name)) {
    result.assignMe = true
    name = name.replace(ME_RE, '$1')
  }

  if (context.labels && context.labels.length > 0) {
    const candidates = context.labels.map((label) => ({
      id: label.id,
      names: [label.name],
    }))
    const labelIds: Array<string> = []
    // Several labels can be typed in one go ("#design #ux ").
    for (let match = findSigilMatch(name, '#', candidates); match; match = findSigilMatch(name, '#', candidates)) {
      if (!labelIds.includes(match.id)) labelIds.push(match.id)
      name = cutSpan(name, match.start, match.end)
    }
    if (labelIds.length > 0) result.labelIds = labelIds
  }

  if (!result.assignMe && context.members && context.members.length > 0) {
    // Full name, first name, or the part of the email before the @.
    const candidates = context.members.map((member) => ({
      id: member.userId,
      names: [
        member.name ?? '',
        member.name?.trim().split(/\s+/)[0] ?? '',
        member.email?.split('@')[0] ?? '',
      ].filter((value) => value.trim().toLowerCase() !== 'me'),
    }))
    const match = findSigilMatch(name, '@', candidates)
    if (match) {
      result.assigneeUserId = match.id
      name = cutSpan(name, match.start, match.end)
    }
  }

  if (context.projects && context.projects.length > 0) {
    // "@me" is reserved for the assignee and was consumed above.
    const candidates = context.projects.map((project) => ({
      id: project.id,
      names: [project.name, project.projectKey ?? ''].filter(
        (value) => value.trim().toLowerCase() !== 'me',
      ),
    }))
    const match = findSigilMatch(name, '@', candidates)
    if (match) {
      result.projectId = match.id
      name = cutSpan(name, match.start, match.end)
    }
  }

  if (context.parseDates && /\s$/.test(name)) {
    const match = detectTrailingDate(name, context.refDate)
    if (
      match &&
      match.hasDay &&
      !AMBIGUOUS_DATE_WORDS.has(match.matchedText.trim().toLowerCase())
    ) {
      result.dueDate = match.date
      if (match.hasTime) result.dueHasTime = true
      name = match.cleanedTitle ? `${match.cleanedTitle} ` : ''
    }
  }

  if (name !== input) name = name.replace(/\s{2,}/g, ' ').replace(/^\s+/, '')
  result.name = name
  return result
}

/** How many properties a parse filled in, for the row's "N parsed" hint. */
export function countParsedTokens(tokens: QuickAddTokens): number {
  return (
    (tokens.priority !== undefined ? 1 : 0) +
    (tokens.assignMe || tokens.assigneeUserId ? 1 : 0) +
    (tokens.labelIds?.length ?? 0) +
    (tokens.projectId ? 1 : 0) +
    (tokens.dueDate ? 1 : 0)
  )
}
